import React, { useState } from 'react';
import DesignLabHeader from './components/DesignLabHeader';
import DashboardCards from './components/DashboardCards';
import TabNavigation from './components/TabNavigation';
import EquipmentTable from './components/EquipmentTable';
import AddEquipmentModal from './components/AddEquipmentModal';
import StyleSwitcher from './components/StyleSwitcher';
import { useDesignVariants } from './useDesignVariants';
import { mockStats, mockEquipment } from './mockData';

/**
 * Design Lab Module - DEV-ONLY realistic UI testing environment
 *
 * A feature-flagged development tool that:
 * - Looks like an actual FleetSkipper module (Equipment/Maintenance style)
 * - Shows real dashboard cards, data tables, modals, and forms
 * - Allows live switching between design variants
 * - Displays exact CSS values for each variant
 *
 * Access: Available in development mode via sidebar navigation
 *
 * Design Variants:
 * 1. Current (Production) - Baseline with colored icon backgrounds
 * 2. Modern (Clean Icons) - No backgrounds, larger icons, strong shadows
 * 3. iOS-style (Thin & Light) - Thin strokes, light weights, rounded corners
 * 4. Rich Colors (Dark) - Dark saturated colors, bold weights, strong shadows
 * 5. Refined (Thin & Dark) - Thinner fonts, darker text colors
 * 6. FleetSkipper (Website Style) - Complete website design system with cyan/navy
 */
const DesignLabModule = () => {
  const [variant, setVariant] = useState('current');
  const [activeTab, setActiveTab] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const styles = useDesignVariants(variant);

  return (
    <div className="min-h-screen bg-gray-50 p-6" style={{ fontFamily: styles.fontFamily || 'inherit' }}>
      {/* Style Switcher - Fixed top-right */}
      <StyleSwitcher
        currentVariant={variant}
        onVariantChange={setVariant}
        styles={styles}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto pr-80"> {/* pr-80 to avoid style switcher overlap */}
        {/* Module Header */}
        <DesignLabHeader
          onAddEquipment={() => setShowModal(true)}
          styles={styles}
        />

        {/* Dashboard Cards */}
        <DashboardCards
          stats={mockStats}
          styles={styles}
        />

        {/* Tab Navigation */}
        <TabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          styles={styles}
        />

        {/* Equipment Table */}
        <EquipmentTable
          equipment={mockEquipment}
          activeTab={activeTab}
          styles={styles}
        />

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          <button className={`px-4 py-2 border ${styles.borderColor} rounded ${styles.shadow} hover:bg-gray-50 transition`}>
            Export
          </button>
          <button className={`px-4 py-2 border ${styles.borderColor} rounded ${styles.shadow} hover:bg-gray-50 transition`}>
            Print
          </button>
          <button className={`px-4 py-2 border ${styles.borderColor} rounded ${styles.shadow} hover:bg-gray-50 transition`}>
            Settings
          </button>
        </div>
      </div>

      {/* Add Equipment Modal */}
      {showModal && (
        <AddEquipmentModal
          onClose={() => setShowModal(false)}
          styles={styles}
        />
      )}
    </div>
  );
};

export default DesignLabModule;
